'use client'

import { useState, useEffect } from 'react'
import { Book, ChevronRight, CheckCircle, Circle, RefreshCw } from 'lucide-react'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import type { SceneInput, SavedScene } from '@creanizer/types'
import { usePlotStore } from '../../../../stores/plotStore'
import { useSceneStore, type SceneChain } from '../../../../stores/sceneStore'
import { ChapterSectionUI } from './ChapterSectionUI'

interface SceneFormContinuationProps {
  input: SceneInput
  onChange: (input: SceneInput) => void
  scenes: SavedScene[]
  currentChainId: string
  sourceType?: 'saved' | 'chapter'
  onSourceTypeChange?: (type: 'saved' | 'chapter') => void
  onChainCreate: (name: string, firstSceneId: string) => void
  onChainSelect?: (chainId: string) => void
  sceneChains?: SceneChain[]
}

export function SceneFormContinuation({ 
  input, 
  onChange, 
  scenes,
  currentChainId,
  sourceType: externalSourceType,
  onSourceTypeChange,
  onChainCreate,
  onChainSelect,
  sceneChains = []
}: SceneFormContinuationProps) {
  console.log('【デバッグ】SceneFormContinuation レンダリング:', {
    currentChainId: currentChainId,
    currentChainIdタイプ: typeof currentChainId,
    currentChainId長さ: currentChainId?.length,
    sceneChainsCount: sceneChains.length,
    sceneChains: sceneChains.map(c => ({ id: c.id, name: c.name, sceneCount: c.sceneIds.length }))
  })
  
  const [selectedSceneId, setSelectedSceneId] = useState<string>(() => {
    const initialId = input.previousSceneId || ''
    console.log('【デバッグ】selectedSceneId初期値:', {
      fromInput: input.previousSceneId,
      initialValue: initialId
    })
    return initialId
  })
  const [chainName, setChainName] = useState<string>('')
  const [showChainCreate, setShowChainCreate] = useState(false)
  const [sourceType, setSourceType] = useState<'saved' | 'chapter'>(externalSourceType || 'saved')
  const [selectedChainId, setSelectedChainId] = useState<string>('')
  const [showChapterSetup, setShowChapterSetup] = useState(false)
  const [selectedChapterId, setSelectedChapterId] = useState<string>('')
  const [chapterTitle, setChapterTitle] = useState<string>('')
  const [chapterNumber, setChapterNumber] = useState<number>(1)
  const [targetLength, setTargetLength] = useState<number>(20000)
  const [sectionCount, setSectionCount] = useState<number>(4)
  const [selectedPlotId, setSelectedPlotId] = useState<string>('')
  const [selectedPlotChapter, setSelectedPlotChapter] = useState<number>(0)
  
  const { savedPlots, getPlot } = usePlotStore()
  const { 
    chapterSections, 
    createChapterSection, 
    getChapterSection, 
    getNextPendingSection,
    getCurrentOrNextSection,
    updateSectionStatus,
    updateSectionNotes,
    updateSectionSummary,
    updateSectionTargetLength,
    updateChapterAvoidWords,
    getScene,
    deleteChapterSection,
    markChapterAsCompleted,
    generateChapterSummary,
    resetChapterSummary,
    getCompletedChapters,
    getCumulativeSummary,
    exportChapterAsText,
    exportChapterAsJSON,
    importChapterFromJSON,
    getChainContextScenes
  } = useSceneStore()
  
  const currentChapter = selectedChapterId ? getChapterSection(selectedChapterId) : undefined
  const nextSection = currentChapter ? getCurrentOrNextSection(selectedChapterId) : undefined

  // inputのpreviousSceneIdが変更されたらselectedSceneIdも更新
  useEffect(() => {
    if (input.previousSceneId && input.previousSceneId !== selectedSceneId) {
      console.log('【デバッグ】input.previousSceneId変更検知:', {
        old: selectedSceneId,
        new: input.previousSceneId
      })
      setSelectedSceneId(input.previousSceneId)
    }
  }, [input.previousSceneId])

  const handleSceneSelect = (sceneId: string, chainId?: string) => {
    // chainIdが引数で渡されたらそれを使用、なければpropsのcurrentChainIdを使用
    const effectiveChainId = chainId !== undefined ? chainId : currentChainId
    
    console.log('【デバッグ】handleSceneSelect開始:', {
      sceneId: sceneId,
      scenesCount: scenes.length,
      引数のchainId: chainId,
      propsのcurrentChainId: currentChainId,
      使用するchainId: effectiveChainId,
      chainIdのタイプ: typeof effectiveChainId,
      chainIdが空か: !effectiveChainId
    })
    
    setSelectedSceneId(sceneId)
    const scene = scenes.find(s => s.id === sceneId)
    
    console.log('【デバッグ】シーン検索結果:', {
      found: !!scene,
      sceneId: scene?.id,
      hasResult: !!scene?.result,
      hasFullText: !!scene?.result?.fullText,
      fullTextLength: scene?.result?.fullText?.length || 0,
      fullTextPreview: scene?.result?.fullText?.substring(0, 100) || 'なし'
    })
    
    if (scene) {
      // チェーンに属している場合は、複数シーンのコンテキストを取得
      let contextText = scene.result?.fullText || ''
      
      console.log('【デバッグ】初期contextText:', {
        contextTextLength: contextText.length,
        contextTextPreview: contextText.substring(0, 100) || '空'
      })
      
      console.log('【デバッグ】handleSceneSelect実行:', {
        effectiveChainId: effectiveChainId,
        sceneId: sceneId,
        sceneName: scene.result?.title || 'タイトルなし'
      })
      
      if (effectiveChainId) {
        console.log('【デバッグ】チェーンコンテキスト取得開始:', {
          effectiveChainId: effectiveChainId,
          sceneId: sceneId
        })
        const chainContext = getChainContextScenes(effectiveChainId, sceneId, 5)  // 4つ前まで全文を含める
        console.log('【デバッグ】getChainContextScenes結果:', {
          hasContextText: !!chainContext.contextText,
          contextTextLength: chainContext.contextText?.length || 0,
          fullScenes: chainContext.fullScenes || [],
          fullScenesCount: chainContext.fullScenes?.length || 0,
          summarizedScenes: chainContext.summarizedScenes || [],
          summarizedScenesCount: chainContext.summarizedScenes?.length || 0
        })
        
        if (chainContext.contextText) {
          // チェーンコンテキストがある場合はそれを使用
          contextText = chainContext.contextText + '\n\n【現在のシーン】\n' + (scene.result?.fullText || '')
          
          console.log('【チェーンコンテキスト】使用:', {
            チェーンID: effectiveChainId,
            選択シーンID: sceneId,
            フルテキストシーン数: chainContext.fullScenes.length,
            要約シーン数: chainContext.summarizedScenes.length,
            コンテキスト文字数: contextText.length
          })
        } else {
          console.log('【注意】チェーンコンテキストなし - 単独シーンとして扱う')
        }
      } else {
        console.log('【注意】effectiveChainIdが空 - 単独シーンとして扱う')
      }
      
      console.log('【デバッグ】onChange呼び出し前:', {
        contextTextLength: contextText.length,
        contextTextPreview: contextText.substring(0, 100) || '空',
        sceneInputGenre: scene.input?.genre,
        sceneInputPerspective: scene.input?.perspective
      })
      
      // チェーン内の過去のシーンタイトルを収集
      let previousSceneTitles: string[] = []
      if (effectiveChainId) {
        const chain = sceneChains.find(c => c.id === effectiveChainId)
        if (chain) {
          // 現在選択中のシーンより前のタイトルを収集
          const currentIndex = chain.sceneIds.indexOf(sceneId)
          if (currentIndex > 0) {
            const previousSceneIds = chain.sceneIds.slice(0, currentIndex + 1) // 現在のシーンまで含める
            previousSceneTitles = previousSceneIds
              .map(id => scenes.find(s => s.id === id)?.result?.title)
              .filter((title): title is string => !!title)
            
            console.log('【デバッグ】過去のシーンタイトル収集:', {
              チェーンID: effectiveChainId,
              現在のシーンインデックス: currentIndex,
              収集したタイトル数: previousSceneTitles.length,
              タイトル一覧: previousSceneTitles
            })
          }
        }
      }
      
      // uploadedContentを明示的に保持
      const newSettings = {
        ...input,  // 現在の設定をベースに保持
        previousSceneId: sceneId,
        previousSceneText: contextText,
        previousSceneTitles: previousSceneTitles, // 過去のタイトルを追加
        // 前のシーンの基本設定のみ引き継ぐ（上書きを最小限に）
        genre: scene.input.genre ?? input.genre,
        customGenre: scene.input.customGenre ?? input.customGenre,
        perspective: scene.input.perspective ?? input.perspective,
        writingStyle: scene.input.writingStyle ?? input.writingStyle
        // その他の設定（targetLength、themes、focusElements、uploadedContent等）は現在の値を維持
      }
      
      console.log('【デバッグ】handleSceneSelect - uploadedContent確認:', {
        before: input.uploadedContent ? 'あり' : 'なし',
        after: newSettings.uploadedContent ? 'あり' : 'なし'
      })
      
      onChange(newSettings)
      
      console.log('【デバッグ】onChange呼び出し完了')
    }
  }
  
  // 長編モードでセクション選択時の処理
  useEffect(() => {
    if (sourceType === 'chapter' && currentChapter && nextSection) {
      console.log('選択されたセクション:', {
        chapterId: currentChapter.id,
        sectionNumber: nextSection.sectionNumber,
        status: nextSection.status
      })
      
      // 章コンテキストを構築
      let chapterContext = `第${currentChapter.chapterNumber}章: ${currentChapter.chapterTitle}\n`
      chapterContext += `セクション${nextSection.sectionNumber}/${currentChapter.sections.length}\n`
      
      // revisionステータスの場合、書き直しモードであることを明記
      if (nextSection.status === 'revision') {
        chapterContext += '【書き直しモード】前の内容を改善して書き直してください\n'
        
        // 前の内容があれば参照用に追加
        if (nextSection.fullText) {
          chapterContext += '\n【書き直し前の内容（参考）】\n'
          chapterContext += nextSection.fullText.substring(0, 500) + '...\n'
        } else if (nextSection.sceneId) {
          const previousScene = getScene(nextSection.sceneId)
          if (previousScene) {
            chapterContext += '\n【書き直し前の内容（参考）】\n'
            chapterContext += (previousScene.result?.fullText || '').substring(0, 500) + '...\n'
          }
        }
        
        // メモがあれば追加
        if (nextSection.notes) {
          chapterContext += `\n【メモ・修正指示】\n${nextSection.notes}\n`
        }
      }
      chapterContext += '\n'
      
      // プロット連携情報があれば追加
      if (currentChapter.plotId && currentChapter.plotChapterNumber) {
        const plot = getPlot(currentChapter.plotId)
        if (plot && plot.result?.structure) {
          const plotChapter = plot.result.structure.find((ch: any) => ch.number === currentChapter.plotChapterNumber)
          if (plotChapter) {
            chapterContext += `【プロット情報】\n`
            chapterContext += `章タイトル: ${plotChapter.title}\n`
            chapterContext += `章の概要: ${plotChapter.summary}\n`
            // プロットにシーン情報が含まれる場合（拡張データ）
            const plotChapterWithScenes = plotChapter as any
            if (plotChapterWithScenes.scenes && plotChapterWithScenes.scenes.length > 0) {
              chapterContext += `\n予定されているシーン:\n`
              plotChapterWithScenes.scenes.forEach((scene: any, index: number) => {
                chapterContext += `${index + 1}. ${scene.description}\n`
              })
            }
            chapterContext += '\n'
          }
          
          // 物語全体の概要も追加
          if (plot.result?.premise) {
            chapterContext += `【物語の前提】\n${plot.result.premise}\n\n`
          }
        }
      }
      
      // 前章までの累積要約があれば追加
      if (currentChapter.previousChaptersSummary) {
        chapterContext += `【今までのあらすじ】\n${currentChapter.previousChaptersSummary}\n\n`
      }
      
      // 現在のセクションのあらすじがあれば追加
      if (nextSection.summary) {
        chapterContext += `【このセクションのあらすじ】\n${nextSection.summary}\n\n`
      }
      
      // 完了したセクションのタイトル一覧を追加（概要把握用）
      const completedSections = currentChapter.sections
        .filter((s: any) => s.status === 'completed' && s.sectionNumber < nextSection.sectionNumber)
      
      if (completedSections.length > 0) {
        chapterContext += `【この章の進行状況】\n`
        completedSections.forEach((section: any) => {
          // まずセクションに保存されたタイトルを使用、なければsceneIdから取得
          const title = section.title || (section.sceneId ? getScene(section.sceneId)?.result?.title : null)
          if (title) {
            chapterContext += `セクション${section.sectionNumber}: ${title}\n`
          }
        })
        chapterContext += '\n'
      }
      
      // 全ての完了セクションの完全テキストを連結
      let allPreviousText = ''
      if (nextSection.sectionNumber > 1) {
        // セクション1から現在のセクションの直前まで、すべての完了セクションを取得
        const allCompletedSections = currentChapter.sections
          .filter((s: any) => s.status === 'completed' && s.sectionNumber < nextSection.sectionNumber)
          .sort((a: any, b: any) => a.sectionNumber - b.sectionNumber)
        
        console.log('【デバッグ】完了セクション数:', allCompletedSections.length)
        console.log('【デバッグ】完了セクション詳細:', allCompletedSections.map((s: any) => ({
          sectionNumber: s.sectionNumber,
          sceneId: s.sceneId,
          status: s.status
        })))
        
        // 各セクションの完全なテキストを連結
        allCompletedSections.forEach((section: any) => {
          console.log(`【デバッグ】セクション${section.sectionNumber}を取得開始:`, {
            hasFullText: !!section.fullText,
            要求するsceneId: section.sceneId
          })
          
          // まずセクションに保存されたfullTextを使用、なければsceneIdから取得
          let text = section.fullText
          let title = section.title
          if (!text && section.sceneId) {
            const scene = getScene(section.sceneId)
            text = scene?.result?.fullText
            title = title || scene?.result?.title
            
            console.log(`【デバッグ】セクション${section.sectionNumber}のシーン:`, {
              要求したsceneId: section.sceneId,
              取得したシーンのID: scene?.id || 'なし',
              IDが一致: scene?.id === section.sceneId,
              sceneFound: !!scene,
              hasFullText: !!(scene?.result?.fullText),
              textLength: scene?.result?.fullText?.length || 0,
              保存時刻: scene?.timestamp ? new Date(scene.timestamp).toLocaleString() : '不明'
            })
          }
          
          if (text) {
            console.log(`【デバッグ】セクション${section.sectionNumber}の内容:`, {
              最初の50文字: text.slice(0, 50),
              最後の50文字: text.slice(-50),
              タイトル: title || '不明',
              取得元: section.fullText ? 'セクション内保存' : 'シーンから取得'
            })
            
            allPreviousText += text
            // セクション間に改行を入れて区切りを明確にする
            allPreviousText += '\n\n'
          } else {
            console.log(`【デバッグ】警告：セクション${section.sectionNumber}のテキストが取得できません`)
          }
        })
        
        // 最後の改行を削除
        allPreviousText = allPreviousText.trim()
        
        console.log('【デバッグ】連結されたテキストの長さ:', allPreviousText.length)
        console.log('【デバッグ】連結されたテキストの最後100文字:', allPreviousText.slice(-100))
        
        if (allPreviousText) {
          const updateData = {
            ...input,  // 既存の設定をすべて保持
            previousSceneId: completedSections[completedSections.length - 1]?.sceneId || '',
            previousSceneText: allPreviousText,
            continuationType: 'direct' as 'direct',
            targetLength: nextSection.targetLength || input.targetLength,  // セクション設定を優先、なければ現在値
            plotContext: chapterContext,
            chapterSection: {
              chapterId: currentChapter.id,
              sectionNumber: nextSection.sectionNumber
            },
            avoidElements: currentChapter.avoidWords || input.avoidElements || []
            // uploadedContentは...inputに含まれるため自動的に維持される
          }
          console.log('【デバッグ】onChange呼び出し:', {
            previousSceneTextLength: updateData.previousSceneText.length,
            chapterSection: updateData.chapterSection,
            avoidElements: updateData.avoidElements
          })
          onChange(updateData)
        } else {
          console.log('【デバッグ】警告：前のセクションのテキストが見つかりません')
        }
      } else {
        // 第1セクションの場合
        // 前章の最終セクションの本文があれば優先的に使用（自然な流れのため）
        const previousText = currentChapter.previousChapterFullText || 
                           currentChapter.previousChaptersSummary || ''
        
        console.log('【第1セクション生成時の引き継ぎ】:', {
          chapterNumber: currentChapter.chapterNumber,
          hasPreviousChapterFullText: !!currentChapter.previousChapterFullText,
          previousChapterFullTextLength: currentChapter.previousChapterFullText?.length || 0,
          previousTextPreview: previousText.substring(0, 100),
          usingType: currentChapter.previousChapterFullText ? 'fullText' : 
                     currentChapter.previousChaptersSummary ? 'summary' : 'none'
        })
        
        // プロットコンテキストに累積要約を含める（伏線情報含む）
        let enhancedPlotContext = chapterContext
        if (currentChapter.cumulativeSummary) {
          enhancedPlotContext = `${chapterContext}\n\n【これまでの物語の流れ】\n${currentChapter.cumulativeSummary}`
        }
        if (currentChapter.previousChapterConnection) {
          enhancedPlotContext += `\n\n【前章との繋がり】\n${currentChapter.previousChapterConnection}`
        }
        
        onChange({
          ...input,  // 既存の設定をすべて保持
          previousSceneId: '',
          previousSceneText: previousText,
          targetLength: nextSection.targetLength || input.targetLength,  // セクション設定を優先、なければ現在値
          plotContext: enhancedPlotContext,
          chapterSection: {
            chapterId: currentChapter.id,
            sectionNumber: nextSection.sectionNumber
          },
          avoidElements: currentChapter.avoidWords || input.avoidElements || []
          // uploadedContentは...inputに含まれるため自動的に維持される
        })
      }
    }
  }, [sourceType, selectedChapterId, currentChapter, nextSection])

  const handleCreateChain = () => {
    if (chainName && selectedSceneId) {
      onChainCreate(chainName, selectedSceneId)
      setShowChainCreate(false)
      setChainName('')
    }
  }

  return (
    <div className="space-y-4">
      {/* 前のシーン選択 */}
      <div>
        <Label>続きを書くシーンの選択方法</Label>
        <Tabs value={sourceType} onValueChange={(v) => {
          const newType = v as 'saved' | 'chapter'
          setSourceType(newType)
          if (onSourceTypeChange) {
            onSourceTypeChange(newType)
          }
        }} className="mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="saved">保存済みシーン</TabsTrigger>
            <TabsTrigger value="chapter">長編執筆</TabsTrigger>
          </TabsList>
          
          <TabsContent value="saved" className="mt-4">
            <div className="space-y-4">
              {/* 既存チェーンの選択（オプション） */}
              {sceneChains.length > 0 && (
                <div>
                  <Label htmlFor="chainSelect">既存のシーンチェーンを継続（オプション）</Label>
                  <Select
                    value={selectedChainId || 'new'}
                    onValueChange={(value) => {
                      if (value === 'new') {
                        // 新しいチェーンを開始
                        setSelectedChainId('')
                        if (onChainSelect) {
                          onChainSelect('')
                        }
                      } else {
                        // 既存のチェーンを選択
                        setSelectedChainId(value)
                        if (onChainSelect) {
                          onChainSelect(value)
                        }
                        // チェーンの最後のシーンを自動選択
                        const chain = sceneChains.find(c => c.id === value)
                        if (chain && chain.sceneIds.length > 0) {
                          const lastSceneId = chain.sceneIds[chain.sceneIds.length - 1]
                          setSelectedSceneId(lastSceneId)
                          
                          // 保存された設定を復元（handleSceneSelectより先に実行）
                          if (chain.lastSettings) {
                            console.log('【デバッグ】チェーン設定復元（handleSceneSelect前）', {
                              hasUploadedContent: 'uploadedContent' in (chain.lastSettings as any),
                              currentUploadedContent: input.uploadedContent ? 'あり' : 'なし'
                            })
                            const s = chain.lastSettings as any
                            onChange({
                              ...input,
                              // 基本設定
                              continuationType: s.continuationType ?? input.continuationType,
                              genre: s.genre ?? input.genre,
                              customGenre: s.customGenre ?? input.customGenre,
                              perspective: s.perspective ?? input.perspective,
                              writingStyle: s.writingStyle ?? input.writingStyle,
                              customStyle: s.customStyle ?? input.customStyle,
                              savedCustomStyleId: s.savedCustomStyleId ?? input.savedCustomStyleId,
                              
                              // シーン設定
                              sceneType: s.sceneType ?? input.sceneType,
                              customType: s.customType ?? input.customType,
                              atmosphere: s.atmosphere ?? input.atmosphere,
                              customAtmosphere: s.customAtmosphere ?? input.customAtmosphere,
                              
                              // 生成設定
                              targetLength: s.targetLength ?? input.targetLength,
                              lengthType: s.lengthType ?? input.lengthType,
                              creativityLevel: s.creativityLevel ?? input.creativityLevel,
                              variationMode: s.variationMode ?? input.variationMode,
                              useProMode: s.useProMode ?? input.useProMode,
                              useProLiteMode: s.useProLiteMode ?? input.useProLiteMode,
                              
                              // 内容設定
                              focusElements: s.focusElements ?? input.focusElements,
                              avoidElements: s.avoidElements ?? input.avoidElements,
                              emotionalTone: s.emotionalTone ?? input.emotionalTone,
                              themes: s.themes ?? input.themes,
                              
                              // その他
                              additionalSettings: s.additionalSettings ?? input.additionalSettings
                              // uploadedContentは常に現在の値を使用（チェーン設定とは独立）
                              // input.uploadedContentは...inputで既に含まれている
                            })
                          }
                          
                          // 設定復元後にシーンを選択（chainIdを引数として渡す）
                          setTimeout(() => {
                            console.log('【デバッグ】handleSceneSelect呼び出し（遅延実行）', {
                              lastSceneId: lastSceneId,
                              chainId: value
                            })
                            handleSceneSelect(lastSceneId, value)
                          }, 0)
                        }
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="チェーンを選択して継続" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      <SelectItem value="new">新しいチェーンを開始</SelectItem>
                      {sceneChains.map(chain => (
                        <SelectItem key={chain.id} value={chain.id}>
                          {chain.name} (シーン数: {chain.sceneIds.length})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label htmlFor="previousScene">続きを書くシーン</Label>
                <Select
                  value={selectedSceneId}
                  onValueChange={handleSceneSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="シーンを選択" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {scenes.length === 0 ? (
                      <SelectItem value="none" disabled>
                        保存されたシーンがありません
                      </SelectItem>
                    ) : (
                      scenes.map(scene => {
                        // resultがない古いデータの場合のフォールバック
                        const title = scene.result?.title || (scene as any).title || `シーン ${scene.id.substring(0, 8)}`
                        return (
                          <SelectItem key={scene.id} value={scene.id}>
                            {title} ({new Date(scene.timestamp).toLocaleDateString()})
                          </SelectItem>
                        )
                      })
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* 出力文字数設定 */}
              {selectedSceneId && (
                <div>
                  <Label htmlFor="target-length-saved">目標文字数</Label>
                  <Input
                    id="target-length-saved"
                    type="number"
                    min="100"
                    max="30000"
                    step="100"
                    value={input.targetLength || 3000}
                    onChange={(e) => onChange({
                      ...input,
                      targetLength: parseInt(e.target.value) || 3000
                    })}
                    placeholder="例: 3000"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    100〜30000文字の範囲で設定可能
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="chapter" className="mt-4">
            <ChapterSectionUI
              chapterSections={chapterSections}
              selectedChapterId={selectedChapterId}
              setSelectedChapterId={setSelectedChapterId}
              showChapterSetup={showChapterSetup}
              setShowChapterSetup={setShowChapterSetup}
              useProMode={input.useProMode}
              savedPlots={savedPlots}
              getPlot={getPlot}
              createChapterSection={createChapterSection}
              getChapterSection={getChapterSection}
              getNextPendingSection={getNextPendingSection}
              getCurrentOrNextSection={getCurrentOrNextSection}
              updateSectionStatus={updateSectionStatus}
              updateSectionNotes={updateSectionNotes}
              updateSectionSummary={updateSectionSummary}
              updateSectionTargetLength={updateSectionTargetLength}
              updateChapterAvoidWords={updateChapterAvoidWords}
              getScene={getScene}
              deleteChapterSection={deleteChapterSection}
              markChapterAsCompleted={markChapterAsCompleted}
              generateChapterSummary={generateChapterSummary}
              resetChapterSummary={resetChapterSummary}
              getCompletedChapters={getCompletedChapters}
              getCumulativeSummary={getCumulativeSummary}
              exportChapterAsText={exportChapterAsText}
              exportChapterAsJSON={exportChapterAsJSON}
              importChapterFromJSON={importChapterFromJSON}
              onReferenceChange={(content) => {
                onChange({ ...input, uploadedContent: content })
              }}
              initialReferenceContent={input.uploadedContent}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* 選択したシーンのプレビュー */}
      {selectedSceneId && input.previousSceneText && (
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-semibold mb-2">
            {currentChainId && sceneChains.find(c => c.id === currentChainId)?.sceneIds.length > 1 ? 
              'シーンチェーンのプレビュー' : '前のシーンのプレビュー'}
          </h4>
          {currentChainId && sceneChains.find(c => c.id === currentChainId)?.sceneIds.length > 1 ? (
            // チェーンの場合：最初と最後を表示
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">【チェーンの冒頭】</p>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {(() => {
                    // チェーンの最初のシーンを取得
                    const chain = sceneChains.find(c => c.id === currentChainId)
                    if (!chain || chain.sceneIds.length === 0) return ''
                    const firstSceneId = chain.sceneIds[0]
                    const firstScene = scenes.find(s => s.id === firstSceneId)
                    return firstScene?.result?.fullText?.substring(0, 300) + '...' || ''
                  })()}
                </p>
              </div>
              <div className="border-t pt-2">
                <p className="text-xs text-muted-foreground mb-1">【直前のシーンの終わり】</p>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {(() => {
                    const lastPart = input.previousSceneText?.slice(-400)
                    const startPos = lastPart?.indexOf('。') || 0
                    return '...' + (lastPart?.substring(startPos + 1) || '')
                  })()}
                </p>
              </div>
              <div className="text-xs text-muted-foreground">
                チェーン内のシーン数: {sceneChains.find(c => c.id === currentChainId)?.sceneIds.length || 0}
              </div>
            </div>
          ) : (
            // 通常の場合：従来通り冒頭のみ表示
            <p className="text-sm text-muted-foreground line-clamp-5">
              {input.previousSceneText?.substring(0, 500)}...
            </p>
          )}
        </div>
      )}

      {/* 継続タイプ */}
      <div>
        <Label htmlFor="continuationType">継続タイプ</Label>
        <Select
          value={input.continuationType || 'direct'}
          onValueChange={(value) => onChange({ ...input, continuationType: value as 'direct' | 'timeskip' | 'perspective' | 'parallel' })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            <SelectItem value="direct">直接の続き（すぐ次の場面）</SelectItem>
            <SelectItem value="timeskip">時間経過後（数時間〜数日後）</SelectItem>
            <SelectItem value="perspective">視点変更（別キャラクターの視点）</SelectItem>
            <SelectItem value="parallel">同時進行（同じ時間の別の場所）</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* 継続タイプに応じた追加情報 */}
      {input.continuationType === 'timeskip' && (
        <div>
          <Label htmlFor="timeskipInfo">経過時間</Label>
          <Input
            id="timeskipInfo"
            value={input.timeskipDescription || ''}
            onChange={(e) => onChange({ ...input, timeskipDescription: e.target.value })}
            placeholder="例：3日後、1週間後の朝"
          />
        </div>
      )}

      {input.continuationType === 'perspective' && (
        <div>
          <Label htmlFor="perspectiveInfo">新しい視点のキャラクター</Label>
          <Input
            id="perspectiveInfo"
            value={input.perspectiveCharacter || ''}
            onChange={(e) => onChange({ ...input, perspectiveCharacter: e.target.value })}
            placeholder="例：敵の視点から、第三者の視点から"
          />
        </div>
      )}

      {input.continuationType === 'parallel' && (
        <div>
          <Label htmlFor="parallelInfo">同時進行の場所・状況</Label>
          <Input
            id="parallelInfo"
            value={input.parallelDescription || ''}
            onChange={(e) => onChange({ ...input, parallelDescription: e.target.value })}
            placeholder="例：その頃、城の地下牢では..."
          />
        </div>
      )}

      {/* 続きの概要 */}
      <div>
        <Label htmlFor="continuationSummary">続きのシーンの概要（オプション）</Label>
        <Textarea
          id="continuationSummary"
          value={input.sceneSummary || ''}
          onChange={(e) => onChange({ ...input, sceneSummary: e.target.value })}
          placeholder="続きのシーンで起こることを説明（空欄の場合はAIが自動判断）"
          rows={4}
        />
      </div>

      {/* シーンチェーン管理 */}
      {sourceType === 'saved' && (
        <div className="border-t pt-4">
          <h4 className="font-semibold mb-2">シーンチェーン管理</h4>
          {currentChainId ? (
            <Alert>
              <AlertDescription>
                現在のチェーンに追加されます
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              {!showChainCreate ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowChainCreate(true)}
                  disabled={!selectedSceneId}
                >
                  新しいシーンチェーンを作成
                </Button>
              ) : (
              <div className="space-y-2">
                <Input
                  value={chainName}
                  onChange={(e) => setChainName(e.target.value)}
                  placeholder="チェーン名（例：第1章、プロローグ）"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={handleCreateChain}
                    disabled={!chainName}
                  >
                    作成
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowChainCreate(false)
                      setChainName('')
                    }}
                  >
                    キャンセル
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* 追加設定への誘導 */}
      <Alert>
        <AlertDescription>
          視点、時制、文体などの設定は前のシーンから自動的に引き継がれます。
          変更したい場合は、下部の詳細設定から調整してください。
        </AlertDescription>
      </Alert>
    </div>
  )
}